import { forwardRef, useCallback, useState } from 'react';
import { Box, Drawer, IconButton, Tooltip, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import type { AiChatDrawerProps, AiChatHandle } from './AiChatTypes';
import AiChat from './AiChat';

const AiChatDrawer = forwardRef<AiChatHandle, AiChatDrawerProps>((props, ref) => {
    const {
        open,
        onClose,
        anchor = 'right',
        width = '40vw',
        title,
        onVisibleQuestionChange: externalOnVisibleQuestionChange,
        ...aiChatProps
    } = props;

    const [visibleQuestion, setVisibleQuestion] = useState<string | null>(null);

    const handleVisibleQuestionChange = useCallback((question: string | null) => {
        setVisibleQuestion(question);
        externalOnVisibleQuestionChange?.(question);
    }, [externalOnVisibleQuestionChange]);

    const displayTitle = visibleQuestion || title || 'Chat';

    return (
        <Drawer
            anchor={anchor}
            open={open}
            onClose={onClose}
            ModalProps={{ keepMounted: true }}
            slotProps={{
                paper: {
                    sx: {
                        width,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }
                },
            }}
        >
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
                <Tooltip title={displayTitle} enterDelay={500}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            flex: 1,
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {displayTitle}
                    </Typography>
                </Tooltip>
                {onClose && (
                    <IconButton size="small" onClick={onClose} edge="end" sx={{ ml: 1, flexShrink: 0 }}>
                        <Close fontSize="small" />
                    </IconButton>
                )}
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <AiChat ref={ref} {...aiChatProps} onVisibleQuestionChange={handleVisibleQuestionChange} />
            </Box>
        </Drawer>
    );
});

AiChatDrawer.displayName = 'AiChatDrawer';

export { AiChatDrawer };
